import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { backofficeUrl, T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { AiOutlineButton } from '../ai';
import { AgentAddonActionCard } from './AgentAddonActionCard';
import { AgentRequirementControls } from './AgentRequirementControls';
import { useAgentAssistant } from './AgentAssistantContext';
import { useAICreditsPolicyGate } from '../aiCreditsPolicy/aiCreditsPolicyDialog';

export const AgentAccountRequirementCard: FC<{ action: string; busy: boolean; hasDraft?: boolean; onResume: () => void | Promise<void> }> = ({
    action,
    busy,
    onResume,
    hasDraft = false
}) => {
    const navigate = useNavigate();
    const { close } = useAgentAssistant();
    const { isAllowed } = usePermissions();
    const { ensureAICreditsPolicyAccepted } = useAICreditsPolicyGate();
    // The agent is about to generate images (they consume AI credits) and the AI credits policy is not accepted yet:
    // the same blocking dialog of the other AI tools, then the agent resumes the work.
    if (action === 'accept_ai_credits_policy')
        return (
            <AgentRequirementControls disabled={busy}>
                <span>{T._('Accept the AI credits policy to let the agent generate images.', 'Admin')}</span>
                <AiOutlineButton
                    type='button'
                    $size='small'
                    disabled={busy}
                    onClick={() =>
                        void ensureAICreditsPolicyAccepted().then((accepted) => {
                            if (accepted) void onResume();
                        })
                    }
                >
                    {T._('Review and accept', 'Admin')}
                </AiOutlineButton>
            </AgentRequirementControls>
        );
    // Too many AI requests right now (rate limit of SubscriptionService): the reason is not shown, only that AI is
    // temporarily unavailable; checking again resumes once the window has passed.
    if (action === 'ai_temporarily_unavailable')
        return (
            <AgentRequirementControls disabled={busy}>
                <span>{T._('AI features are temporarily unavailable. Please try again later.', 'Admin')}</span>
                <AiOutlineButton type='button' $size='small' disabled={busy} onClick={() => void onResume()}>
                    {T._('Check again', 'Admin')}
                </AiOutlineButton>
            </AgentRequirementControls>
        );
    if (action === 'free_credits_used_up')
        return <AgentAddonActionCard addonKey='AI_ACCOUNT' action={action} busy={busy} onResume={onResume} hasDraft={hasDraft} />;
    if (action !== 'manage_subscription') return null;
    const canManage = isAllowed(['SUBSCRIPTION']);
    return (
        <AgentRequirementControls disabled={busy}>
            <span>
                {canManage
                    ? T._('Your subscription is not active. Review your subscription to use the AI tools.', 'Admin')
                    : T._('Ask the account owner or a user with subscription permissions to review the subscription.', 'Admin')}
            </span>
            {canManage && (
                <AiOutlineButton
                    type='button'
                    $size='small'
                    disabled={busy}
                    onClick={() => {
                        close();
                        navigate(backofficeUrl('/seller/subscription'));
                    }}
                >
                    {T._('Manage subscription', 'Admin')}
                </AiOutlineButton>
            )}
            <AiOutlineButton type='button' $size='small' disabled={busy} onClick={() => void onResume()}>
                {T._('Check again', 'Admin')}
            </AiOutlineButton>
        </AgentRequirementControls>
    );
};
